import { reportError } from "$lib/errorHandling/errorReporter";
import { createFetchWithRetry } from "$lib/utils/fetchHelpers";
import { logger } from "$lib/utils/logger";
import { CacheStorageManager } from "$lib/utils/cacheStorageManager";
import { determineProvider, Providers, type Provider } from "./providerHelpers";
import { DateHelper } from "$lib/utils/dateHelper";
import {
  getResponseBody,
  recordPopEvent,
} from "$lib/dataPipeline/dataPipelineManager";
import type { Environment, PopStatus } from "$lib/dataPipeline/eventTypes";

interface PopItem {
  url: string;
  timestamp: number;
}

let isPreview = false;
let isProdBuild = false;
let shouldSendPops = false; // enabled by initializePopManager (Pop.svelte)

const fetch = createFetchWithRetry({
  logContext: "PopManager",
  maxRetriesCount: 8,
});
let popCache: CacheStorageManager<PopItem> = new CacheStorageManager<PopItem>(
  "popCache"
); //NOTE window/localstorage may not be available at import time. re-initialized in initializePopManager.

function initializePopManager(
  elev8Environment: Environment = "production",
  cacheManager?: CacheStorageManager<PopItem>
): CacheStorageManager<PopItem> {
  updatePopEnvironment(elev8Environment);
  popCache = cacheManager ?? new CacheStorageManager<PopItem>("popCache");
  retryCachedItems();
  logger.info("[PopManager] Initialized POP Manager.");
  return popCache;
}

function updatePopEnvironment(elev8Environment: Environment): void {
  isPreview = elev8Environment === "preview";
  isProdBuild = import.meta.env.MODE === "production";
  shouldSendPops = isProdBuild && !isPreview;
  logger.info(
    `[PopManager] POPs ${shouldSendPops ? "enabled" : "disabled"} (${isProdBuild ? "PROD" : "DEV/TEST"} build, ${isPreview ? "preview" : "not preview"})`
  );
}

function cleanupPopManager(): void {
  const num = popCache.getCache().length;
  logger.info(`[PopManager] Cleanup: retryCachedItems - ${num} items`);
  retryCachedItems();
}

function contentPlayed(popUrl: string): void {
  const timestamp = DateHelper.nowWithServerOffset();
  const cacheItem: PopItem = { url: popUrl, timestamp };
  if (
    popCache.getCache().some(({ timestamp: ts }) => ts === timestamp) // keyed on timestamp alone — pops in the same ms collide even across different URLs
  ) {
    // Keep the message free of volatile parts (timestamp/item) — AppSignal
    // groups incidents by message, so interpolation opens an incident per
    // occurrence instead of one incident with a count.
    reportError(
      "[PopManager] Duplicate pop item detected in cache",
      new Error("Duplicate pop item"),
      { url: popUrl, timestamp: String(timestamp) }
    );
    return;
  }
  popCache.appendToCache(cacheItem);
  callUrl(cacheItem).catch((error) => {
    reportError("[PopManager] Failed to call pop URL", error, {
      url: popUrl,
      timestamp: String(timestamp),
    });
  });
}

async function callUrl(cacheItem: PopItem): Promise<void> {
  const { url, timestamp } = cacheItem;
  let provider: Provider = "unknown";
  let status: PopStatus = "UNKNOWN";
  let statusMessage = "";
  let responseBody: string | unknown = "";
  try {
    if (!shouldSendPops) {
      logger.warn(
        `[PopManager] Skipping call pop: preview ${isPreview}, prodBuild ${isProdBuild}, url ${url}`
      );
      return;
    }
    provider = determineProvider(url);
    if (provider === Providers.FAKE) {
      logger.info(`[PopManager] Skipping call to <FAKE> provider URL: ${url}`);
      return;
    }
    if (provider === Providers.UNKNOWN) {
      logger.warn(`[PopManager] Unrecognized provider, sending GET: ${url}`);
    }
    const response = await fetch(url, { method: "GET" });
    responseBody = await getResponseBody(response);
    statusMessage = `${response.status} ${response.statusText}`;
    if (!response.ok) {
      status = "NOT_OK";
      if (response.status === 400) {
        logger.warn(`[PopManager] Pop URL returned 400: ${url}`);
        return;
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    status = "OK";
    logger.debug(`[PopManager] Successfully called pop: ${url}/${timestamp}`);
  } catch (error) {
    status = status === "UNKNOWN" ? "FAIL" : status;
    reportError("[PopManager] Failed to call pop URL", error, {
      url,
      timestamp: String(timestamp),
    });
  } finally {
    recordPopEvent({
      provider,
      status,
      statusMessage,
      response: responseBody,
      callback_url: url,
    });
    popCache.removeFromCache(cacheItem);
  }
}

function retryCachedItems(): void {
  try {
    popCache.getCache().forEach((cacheItem) => {
      logger.info(`[PopManager] Retrying cached URL: ${cacheItem.url}`);
      callUrl(cacheItem);
    });
  } catch (error) {
    reportError(`[PopManager] Failed to retry cached items. Error: `, error);
  }
}

export { type PopItem, initializePopManager, cleanupPopManager, contentPlayed };
